import { useEffect, useState } from 'react'
import { FormProvider, useForm, type FieldErrors, type Resolver } from 'react-hook-form'
import { Box, useMediaQuery } from '@mui/material'

import { RowBox } from '@components/shared/RowBox'
import { Input } from '@components/shared/Input'
import { EditIcon } from '@assets/svgs/EditIcon'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { setInitialValues } from './helpers/setInitialValues'
import { ReturnModal } from '@components/modals/ReturnModal'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { Textarea } from '@components/shared/Textarea'
import { PlusCircleIcon } from '@assets/svgs/PlusCircleIcon'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { Select } from '@components/shared/Select'
import { addEditAdditionalExpensesModalSchema } from './form/validations'
import { useOtherExpenses } from '@pages/OtherExpenses/context'
import { unMaskCurrency } from '@utils/masks/unMaskCurrency'
import { useUser } from '@hooks/useUser'

import type { AddEditAdditionalExpensesModalFormProps } from './form/types'
import type { AddRditOtherConsumptionsModalProps } from './types'

import * as S from './styles'

export const AddEditAdditionalExpenses = ({
  data,
  open,
  onClose,
  canEdit = true,
  ...rest
}: AddRditOtherConsumptionsModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const { filiData } = useUser()

  const {
    additionalExpensesData,
    additionalExpensesLoading,
    isAddEditAdditionalExpensesLoading,
    createAdditionalExpenses,
    editAdditionalExpenses
  } = useOtherExpenses()

  const methods = useForm<AddEditAdditionalExpensesModalFormProps>({
    defaultValues: initialFormValues,
    resolver: addEditAdditionalExpensesModalSchema as unknown as Resolver<AddEditAdditionalExpensesModalFormProps>
  })

  const [openCancelDetailedSummaryModal, setOpenCancelDetailedSummaryModal] = useState(false)

  const accountDescriptionOptions = additionalExpensesData?.availableFilters?.expenseTypes
    ?.map((option) => ({
      label: option.description,
      value: option.accountingCode
    }))
    .filter((option) => option.value !== 'Todos')

  const onError = (errors: FieldErrors<AddEditAdditionalExpensesModalFormProps>) => console.log({ errors })

  const onSubmit = (formData: AddEditAdditionalExpensesModalFormProps) => {
    if (!canEdit) return

    formData.filiCode = filiData?.filiCode!
    formData.competenceDate = filiData?.startPeriod!

    if (!isEdit) {
      createAdditionalExpenses(formData).then((success) => success && onClose(false))
    } else {
      formData.id = data?.id ? Number(data.id) : null

      editAdditionalExpenses(formData).then((success) => success && onClose(false))
    }
  }

  const isEdit = !!data

  useEffect(() => {
    !!data && methods.reset(setInitialValues(data!))
  }, [data, open])

  useEffect(() => {
    !open && methods.reset(initialFormValues)
  }, [open])

  return (
    <>
      <BaseModal2
        open={open}
        width={isMobile ? 'calc(100% - 20px)' : 870}
        heightContainer={isMobile ? 'calc(100% - 20px)' : 'auto '}
        title={isEdit ? 'Editar Despesa Adicional' : 'Adicionar Despesa Adicional'}
        headerIcon={isEdit ? <EditIcon /> : <PlusCircleIcon />}
        onClose={() => setOpenCancelDetailedSummaryModal(true)}
        footer={{
          cancelButtonText: 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: canEdit ? 'Confirmar' : undefined,
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: canEdit ? () => methods.handleSubmit(onSubmit, onError)() : undefined,
          loading: isAddEditAdditionalExpensesLoading
        }}
        {...rest}
      >
        <S.Title>
          {!isEdit
            ? 'Preencha todos os campos para adicionar uma nova despesa.'
            : 'Preencha todos os campos antes de confirmar a edição da despesa.'}
        </S.Title>

        {isEdit && (
          <S.Warning>
            Atenção: Ao confirmar esta edição, todos os dados do serviço previamente registrados serão excluídos.
          </S.Warning>
        )}

        <FormProvider {...methods}>
          <S.Form>
            <RowBox columns={4}>
              <Box sx={{ gridColumn: 'span 2' }}>
                <Select
                  required
                  name="accountDescription"
                  label="Descrição Conta"
                  placeholder="Selecione a desc. da conta"
                  options={!!accountDescriptionOptions?.length ? accountDescriptionOptions : []}
                  onChange={({ target }) => {
                    const accountDescriptionText = accountDescriptionOptions?.find(
                      (option) => option.value === target.value
                    )?.label

                    methods.setValue('accountDescriptionText', accountDescriptionText as string)
                    methods.setValue('accountDescription', target.value as string)
                    methods.setValue('accountNumber', target.value as string)
                  }}
                  loading={additionalExpensesLoading}
                  disabled={!canEdit || isAddEditAdditionalExpensesLoading}
                />
              </Box>
              <Input name="accountNumber" label="Conta" placeholder="Nº da Conta" required disabled />
              <Input
                required
                name="additionalExpensesValue"
                label="Valor Despesa Adicional"
                placeholder="R$ 0,00"
                maskType="currency"
                onBlur={({ target }) => {
                  methods.setValue('additionalExpensesValue', unMaskCurrency(target.value).toFixed(2))
                }}
                disabled={!canEdit || isAddEditAdditionalExpensesLoading}
              />
            </RowBox>

            <RowBox>
              <Textarea
                required
                name="observations"
                label="Observações"
                placeholder="Descreva as suas observações"
                disabled={!canEdit || isAddEditAdditionalExpensesLoading}
              />
            </RowBox>
          </S.Form>
        </FormProvider>
      </BaseModal2>

      <ReturnModal
        open={openCancelDetailedSummaryModal}
        headerIcon={<WarningIcon />}
        title="Deseja realmente cancelar a ação?"
        message="Todo o seu progresso será perdido!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelDetailedSummaryModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelDetailedSummaryModal(false)
        }}
      />
    </>
  )
}
